"use client";

import { useState } from "react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Command, CommandGroup, CommandItem, CommandList } from "@perceptron/ui/components/command";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { Label } from "@perceptron/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@perceptron/ui/components/popover";
import { SelectButton, SelectButtonLabel, SelectButtonValue } from "@perceptron/ui/components/selectButton";
import { Switch } from "@perceptron/ui/components/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@perceptron/ui/components/tooltip";
import { cn } from "@perceptron/utils/cn";

import ToggleButton from "@demo/components/PromptControls/ToggleButton";
import { DetectOutput, useDetectStore } from "@demo/store/detect";

interface OutputOption {
  value: DetectOutput;
  label: string;
  icon: IconName;
  description: string;
}

// Box/Point reuse the chip + on-image icons so the switcher reads the same as the results.
const OUTPUT_OPTIONS: OutputOption[] = [
  {
    value: "box",
    label: strings.detect.output.box.label,
    icon: "box-icon",
    description: strings.detect.output.box.description,
  },
  {
    value: "point",
    label: strings.detect.output.point.label,
    icon: "crosshair",
    description: strings.detect.output.point.description,
  },
];

// Click-driven popover (touch-reliable) for the inline help on a setting — mirrors Compose's Settings.
const SettingsTooltip = ({ content }: { content: string }) => (
  <Popover>
    <PopoverTrigger
      aria-label="More info"
      className="inline-flex size-4 items-center justify-center text-foreground-secondary"
    >
      <Icon className="size-4" name="help-circle" />
    </PopoverTrigger>
    <PopoverContent className="max-w-64 p-2 text-xs" side="top">
      {content}
    </PopoverContent>
  </Popover>
);

const DrilldownRow = ({
  label,
  value,
  valueIcon,
  onClick,
}: {
  label: string;
  value: string;
  valueIcon?: IconName;
  onClick: () => void;
}) => (
  <Button
    className="flex w-full items-center justify-between gap-6 rounded-md px-0 hover:bg-background-tint"
    type="button"
    variant="ghost"
    onClick={onClick}
  >
    <span className="whitespace-nowrap body">{label}</span>
    <div className="flex min-w-0 items-center gap-1">
      <div className="flex items-center gap-1 truncate rounded-md bg-background p-2">
        {valueIcon && <Icon className="size-4 shrink-0" name={valueIcon} />}
        <span className="truncate legal">{value}</span>
      </div>
      <Icon className="size-4 shrink-0" name="chevron-right" />
    </div>
  </Button>
);

const SubViewHeader = ({ title, onBack }: { title: string; onBack: () => void }) => (
  <div className="flex items-center gap-1 px-2 py-2">
    <Button aria-label="Back" className="mr-1 px-0" variant="ghost" onClick={onBack}>
      <Icon className="size-4" name="chevron-left" />
    </Button>
    <span className="legal">{title}</span>
  </div>
);

/** The selectable Box/Point list — shared by the desktop popover and the mobile sub-view. */
const DetectOutputContent = ({ onClose }: { onClose?: () => void }) => {
  const outputType = useDetectStore((state) => state.outputType);
  const setOutputType = useDetectStore((state) => state.setOutputType);

  return (
    <Command className="p-0">
      <CommandList className="max-h-none overflow-visible rounded-xl p-0">
        <CommandGroup>
          {OUTPUT_OPTIONS.map((output, index) => (
            <CommandItem
              className={cn(
                "w-full cursor-pointer flex-col items-start rounded-sm p-3 hover:bg-background-primary",
                outputType === output.value && "!bg-background-hover",
                { "mt-1": index > 0 },
              )}
              key={output.value}
              selected={outputType === output.value}
              value={output.value}
              onSelect={() => {
                setOutputType(output.value);
                onClose?.();
              }}
            >
              <div className="flex w-full items-center">
                <span className="body">{output.label}</span>
                <Icon className="ml-auto h-4 w-4" name={output.icon} />
              </div>
              <div className="mt-1 legal">{output.description}</div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

/** Desktop: an inline output switcher + a Think toggle. */
const DesktopDetectSettings = () => {
  const outputType = useDetectStore((state) => state.outputType);
  const enableThinking = useDetectStore((state) => state.enableThinking);
  const setEnableThinking = useDetectStore((state) => state.setEnableThinking);

  const [isOutputOpen, setIsOutputOpen] = useState(false);
  const current = OUTPUT_OPTIONS.find((o) => o.value === outputType) ?? OUTPUT_OPTIONS[0]!;

  return (
    <div className="flex items-center gap-1">
      <Popover open={isOutputOpen} onOpenChange={setIsOutputOpen}>
        <PopoverTrigger asChild>
          <SelectButton role="menuitem">
            <SelectButtonLabel>{strings.detect.outputLabel}</SelectButtonLabel>
            <SelectButtonValue className="bg-background-hover" icon={current.icon}>
              {current.label}
            </SelectButtonValue>
          </SelectButton>
        </PopoverTrigger>
        <PopoverContent align="start" className="max-w-64 p-0" side="top">
          <DetectOutputContent onClose={() => setIsOutputOpen(false)} />
        </PopoverContent>
      </Popover>

      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleButton
            animated="reverse"
            className="px-3 py-2"
            icon="thinking"
            isActive={enableThinking}
            label={strings.settings.thinking.label}
            onClick={() => setEnableThinking(!enableThinking)}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p>{strings.settings.thinking.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

type MobileView = "main" | "format";

/** Mobile: collapse the Think toggle + output switcher into one Settings popover. */
const MobileDetectSettings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<MobileView>("main");

  const outputType = useDetectStore((state) => state.outputType);
  const enableThinking = useDetectStore((state) => state.enableThinking);
  const setEnableThinking = useDetectStore((state) => state.setEnableThinking);

  const current = OUTPUT_OPTIONS.find((o) => o.value === outputType) ?? OUTPUT_OPTIONS[0]!;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Reset to the main view whenever the popover closes so reopening starts fresh.
    if (!open) setView("main");
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          aria-label={strings.detect.settingsLabel}
          className="h-auto rounded-full bg-background-alt-2 p-3 text-foreground-primary shadow-none hover:bg-background-tint data-[state=open]:bg-background-tint"
          size="sm"
          variant="ghost"
        >
          <Icon className="size-4 opacity-90" name="sliders-horizontal" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-w-64 bg-background p-0" side="top">
        {view === "main" && (
          <>
            <div className="px-3 py-2">
              <span className="legal">{strings.detect.settingsLabel}</span>
            </div>
            <div className="rounded-md bg-background-alt-2 p-3">
              {/* Thinking toggle */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Label className="cursor-pointer body" htmlFor="detect-thinking-toggle">
                    {strings.settings.thinking.label}
                  </Label>
                  <SettingsTooltip content={strings.settings.thinking.tooltip} />
                </div>
                <Switch checked={enableThinking} id="detect-thinking-toggle" onCheckedChange={setEnableThinking} />
              </div>

              <div className="mt-1" />

              {/* Output drilldown */}
              <DrilldownRow
                label={strings.detect.outputLabel}
                value={current.label}
                valueIcon={current.icon}
                onClick={() => setView("format")}
              />
            </div>
          </>
        )}

        {view === "format" && (
          <>
            <SubViewHeader title={strings.detect.outputLabel} onBack={() => setView("main")} />
            <DetectOutputContent onClose={() => setIsOpen(false)} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

/**
 * Detect's run settings: an output (Box/Point) switcher and a Think toggle.
 * Inline on desktop; collapsed into a Settings popover on mobile — mirroring the
 * Compose footer's {@link Settings}.
 */
const DetectSettings = () => (
  <>
    <div className="sm:hidden">
      <MobileDetectSettings />
    </div>
    <div className="hidden sm:flex">
      <DesktopDetectSettings />
    </div>
  </>
);

export default DetectSettings;
